import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDrag, useDrop } from 'react-dnd';
import {
    ArrowRightIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    SparklesIcon,
    FunnelIcon,
    TableCellsIcon,
    ListBulletIcon,
    CheckIcon,
    PlayIcon,
    DocumentIcon,
    ArrowDownTrayIcon,
    LockClosedIcon,
    LockOpenIcon,
    ChevronLeftIcon,
    XCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/apiClient';
import MappingRulesPanel from './MappingRulesPanel';
import { EntityMappingV2, FieldMappingTableRow, MappingStatus, ValidationStatus, MappingType, MappingPreviewSnapshot } from '../../types';
import EmployeeIdMappingStep, { EmployeeIdMapping, EmployeeMatchResult } from './EmployeeIdMappingStep';
import ValueMappingModal from './ValueMappingModal';
import MappingPreviewModal from './MappingPreviewModal';
import { WizardComponentProps } from '../../types';

interface SourceField {
    name: string;
    dataType: string;
    sampleValues: string[];
    originFile?: string;
    fileId?: string;
}

interface TargetField {
    name: string;
    label: string;
    dataType: string;
    required: boolean;
    picklistId?: string;
    picklistData?: any;
    defaultValue?: string;
}

interface Entity {
    Step: number;
    Object: string;
    Entity: string;
    Required: boolean;
}

const ItemTypes = {
    SOURCE_FIELD: 'source_field'
};

const isCountrySpecificEntity = (entityName: string) => {
    if (!entityName) return false;
    // Ends with 3 uppercase letters (ISO country code) e.g. PerGlobalInfoCZE
    if (entityName.length > 3 && /^[A-Z]{3}$/.test(entityName.slice(-3))) return true;

    // Explicit list of entities that commonly require filtering by country
    const explicit = [] as string[];
    return explicit.some(e => entityName.includes(e));
};

interface StepItem {
    label: string;
    count?: string;
}

const Stepper = ({ currentStep, steps, onStepClick }: { currentStep: number, steps: StepItem[], onStepClick: (idx: number) => void }) => (
    <nav aria-label="Progress" className="mb-8">
        <ol role="list" className="space-y-4 md:flex md:space-y-0 md:space-x-8">
            {steps.map((step, index) => {
                const isCompleted = currentStep > index;
                const isCurrent = currentStep === index;
                const statusColor = isCurrent ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500';

                return (
                    <li key={step.label} className="md:flex-1 relative">
                        {/* Counter displayed above for desktop (above the border-t) */}
                        <div className={`hidden md:block absolute -top-6 left-0 text-xs font-bold ${statusColor}`}>
                            {step.count}
                        </div>

                        <button
                            onClick={() => onStepClick(index)}
                            className={`group flex flex-col border-l-4 py-2 pl-4 hover:border-blue-600 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4 w-full text-left transition-colors ${isCurrent ? 'border-blue-600' : isCompleted ? 'border-green-600' : 'border-gray-200'
                                }`}
                        >
                            <span className={`text-xs font-semibold uppercase tracking-wide ${statusColor}`}>
                                Step {index + 1}
                                {/* Show counter inline on mobile */}
                                <span className="md:hidden ml-2 font-normal lowercase text-gray-400">
                                    {step.count && `- ${step.count}`}
                                </span>
                            </span>
                            <span className={`text-sm font-medium ${isCurrent ? 'text-gray-900' : 'text-gray-500'}`}>
                                {step.label}
                            </span>
                        </button>
                    </li>
                );
            })}
        </ol>
    </nav>
);

const DraggableSourceField: React.FC<{ field: SourceField }> = ({ field }) => {
    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.SOURCE_FIELD,
        item: { name: field.name, originFile: field.originFile, fileId: field.fileId },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    }), [field]);

    return (
        <div
            ref={drag as unknown as React.LegacyRef<HTMLDivElement>}
            id={`source-field-${field.fileId || 'unknown'}-${field.name}`}
            className={`px-3 py-2 bg-white border border-gray-200 hover:border-blue-300 rounded shadow-sm cursor-grab active:cursor-grabbing transition-all mb-2 flex items-center justify-between group ${isDragging ? 'opacity-50' : ''}`}
        >
            <div className="font-mono text-xs font-medium text-gray-700 truncate" title={field.name}>{field.name}</div>
            <div className="text-[10px] text-gray-400 max-w-[80px] truncate">{field.originFile}</div>
        </div>
    );
};

// Helper to extract picklist options regardless of structure
const extractPicklistOptions = (field: TargetField): any[] => {
    const pd = field.picklistData;
    if (!pd) return [];
    if (Array.isArray(pd)) return pd;

    // Handle wrapped data structure
    const data = pd.data;
    if (!data) return [];

    // Case 1: Configured options (direct array in data)
    if (Array.isArray(data)) return data;

    // Case 2: SFSF Picklist (nested in picklistOptions)
    if (data.picklistOptions && Array.isArray(data.picklistOptions)) {
        return data.picklistOptions;
    }

    return [];
};

const MappingRowV3: React.FC<{
    targetField: TargetField;
    mapping?: FieldMappingTableRow;
    sourceFiles?: any[];
    onDrop: (source: { name: string, fileId?: string }) => void;
    onClear: () => void;
    onOpenValueMapping: () => void;
    onSetConstant: (value: string) => void;
}> = ({ targetField, mapping, sourceFiles, onDrop, onClear, onOpenValueMapping, onSetConstant }) => {
    const [isEditingConstant, setIsEditingConstant] = useState(false);
    const [constantValue, setConstantValue] = useState("");

    const [{ isOver }, drop] = useDrop(() => ({
        accept: ItemTypes.SOURCE_FIELD,
        drop: (item: { name: string, fileId?: string }) => {
            console.log('MappingRowV3 dropped item:', item, 'target:', targetField.name);
            onDrop(item);
        },
        collect: (monitor) => ({
            isOver: !!monitor.isOver(),
        }),
    }), [onDrop, targetField.name]);
    console.log(`MappingRowV3 Target Field ${targetField.name}:`, targetField);
    const isConstant = mapping?.mappingType === MappingType.CONSTANT;
    const isMapped = !!mapping && (!!mapping.sourceField || isConstant);
    const hasPicklist = !!targetField.picklistId || !!targetField.picklistData;
    const picklistOptions = useMemo(() => extractPicklistOptions(targetField), [targetField]);
    const hasOptions = picklistOptions.length > 0;

    const handleSubmitConstant = () => {
        if (constantValue.trim()) {
            onSetConstant(constantValue);
        }
        setIsEditingConstant(false);
        setConstantValue("");
    };

    return (
        <div
            ref={drop as unknown as React.LegacyRef<HTMLDivElement>}
            className={`relative flex items-center p-3 border rounded-lg transition-all mb-2 group
        ${isOver ? 'ring-2 ring-brand-red bg-orange-50 border-brand-red' : ''}
        ${isMapped ? 'bg-white border-brand-navy shadow-sm' : 'bg-gray-50 border-gray-200 border-dashed'}
      `}
        >
            {/* Target Field (Left) */}
            <div className="w-1/3 pr-4 border-r border-gray-100">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 overflow-hidden">
                        <span className="font-semibold text-brand-navy text-sm truncate" title={targetField.label}>{targetField.label}</span>
                        {targetField.required && (
                            <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-brand-red" title="Required"></span>
                        )}
                    </div>
                    {hasPicklist && (
                        <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded border border-gray-200">Picklist</span>
                    )}
                </div>
                <div className="text-[10px] text-gray-400 font-mono mt-0.5 truncate">{targetField.name}</div>
                {targetField.defaultValue && (
                    <div className="text-[10px] text-brand-red mt-0.5 truncate" title={`Default: ${targetField.defaultValue}`}>
                        Def: {targetField.defaultValue}
                    </div>
                )}
            </div>

            {/* Mapping Connector */}
            <div className="px-3 text-gray-300">
                <ArrowRightIcon className={`w-4 h-4 ${isMapped ? 'text-brand-navy' : ''}`} />
            </div>

            {/* Source Field (Middle) */}
            <div className="flex-1 min-w-0 flex items-center h-8">
                {isMapped ? (
                    <div className={`flex-1 flex items-center justify-between px-2 py-1 rounded border ${isConstant ? 'bg-blue-50 border-blue-100 text-blue-800' : 'bg-gray-100 border-gray-300 text-brand-navy'}`}>
                        <div className="flex flex-col min-w-0">
                            <span className="font-mono font-medium text-sm truncate">
                                {isConstant ? `Constant: "${mapping?.constantValue}"` : mapping?.sourceField}
                            </span>
                            {!isConstant && mapping?.sourceFileId && (
                                <span className="text-[9px] text-gray-400 truncate">
                                    from {sourceFiles?.find(f => f.id === mapping.sourceFileId)?.name || 'unknown file'}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                onClear();
                            }}
                            className="ml-2 text-gray-400 hover:text-brand-red"
                        >
                            ×
                        </button>
                    </div>
                ) : isEditingConstant ? (
                    hasOptions ? (
                        <select
                            autoFocus
                            className="w-full text-sm border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 outline-none"
                            value={constantValue}
                            onChange={e => {
                                setConstantValue(e.target.value);
                                if (e.target.value) {
                                    onSetConstant(e.target.value);
                                    setIsEditingConstant(false);
                                    setConstantValue("");
                                }
                            }}
                            onBlur={() => setIsEditingConstant(false)}
                            onKeyDown={e => {
                                if (e.key === 'Escape') setIsEditingConstant(false);
                            }}
                        >
                            <option value="">Select value...</option>
                            {picklistOptions.map((opt: any, idx: number) => {
                                const val = typeof opt === 'string' ? opt : (opt.externalCode || opt.code || opt.value || opt.id);
                                const label = typeof opt === 'string' ? opt : (opt.label || opt.name || val);
                                return <option key={idx} value={val}>{label}</option>;
                            })}
                        </select>
                    ) : (
                        <input
                            autoFocus
                            className="w-full text-sm border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="Enter constant value..."
                            value={constantValue}
                            onChange={e => setConstantValue(e.target.value)}
                            onBlur={handleSubmitConstant}
                            onKeyDown={e => {
                                if (e.key === 'Enter') handleSubmitConstant();
                                if (e.key === 'Escape') setIsEditingConstant(false);
                            }}
                        />
                    )
                ) : (
                    <div className="flex items-center justify-between w-full group/input px-2">
                        <span className="text-gray-400 text-xs italic">Drop source field here</span>
                        <button
                            onClick={() => setIsEditingConstant(true)}
                            className="text-xs text-blue-500 hover:text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity font-medium"
                        >
                            + Constant
                        </button>
                    </div>
                )}
            </div>

            {/* Actions (Right) */}
            <div className="w-32 pl-4 flex items-center justify-end gap-2">
                {isMapped && hasPicklist && !isConstant && (
                    <button
                        onClick={onOpenValueMapping}
                        className="text-xs px-2 py-1 bg-purple-50 text-purple-700 hover:bg-purple-100 rounded border border-purple-200 transition-colors"
                    >
                        Map Values
                    </button>
                )}
                {isMapped && (!hasPicklist || isConstant) && (
                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                )}
            </div>
        </div>
    );
};

interface EntityFilterCondition {
    sourceField: string;
    sourceFileId: string;
    operator: 'IN' | 'NOT_IN';
    values: string[];
}

const EntityFilterSection: React.FC<{
    condition?: EntityFilterCondition;
    onDrop: (source: { name: string, fileId?: string }) => void;
    onClear: () => void;
    onUpdateValues: (values: string[]) => void;
    sourceFiles?: any[];
}> = ({ condition, onDrop, onClear, onUpdateValues, sourceFiles }) => {
    const [{ isOver }, drop] = useDrop(() => ({
        accept: ItemTypes.SOURCE_FIELD,
        drop: (item: { name: string, fileId?: string }) => onDrop(item),
        collect: (monitor) => ({
            isOver: !!monitor.isOver(),
        }),
    }), [onDrop]);

    const [inputValue, setInputValue] = useState(condition?.values.join(', ') || '');

    useEffect(() => {
        setInputValue(condition?.values.join(', ') || '');
    }, [condition]);

    const handleBlur = () => {
        const vals = inputValue.split(',').map(v => v.trim()).filter(v => v);
        onUpdateValues(vals);
    };

    return (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl shadow-sm">
            <div className="flex items-center gap-2 mb-3">
                <FunnelIcon className="w-5 h-5 text-blue-600" />
                <h3 className="text-sm font-bold text-blue-900 uppercase tracking-wider">Global Entity Filter</h3>
                <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200 font-semibold">
                    Condition Field Mapping
                </span>
            </div>

            <p className="text-xs text-blue-700 mb-4 bg-blue-100/50 p-2 rounded">
                Only employees who have one of the values specified below in the mapped source field will be included in the generated files for this entity.
            </p>

            <div className="flex flex-col md:flex-row gap-4 items-start">
                <div
                    ref={drop as any}
                    className={`flex-1 min-w-[300px] p-3 border-2 border-dashed rounded-lg transition-all flex flex-col justify-center min-h-[64px]
                        ${isOver ? 'bg-blue-100 border-blue-400 ring-2 ring-blue-400' : 'bg-white border-blue-200'}
                        ${condition?.sourceField ? 'border-solid border-blue-300' : ''}
                    `}
                >
                    {condition?.sourceField ? (
                        <div className="flex items-center justify-between">
                            <div className="flex flex-col">
                                <div className="text-[10px] font-bold text-blue-400 uppercase tracking-tighter mb-0.5">Filtered Source Field:</div>
                                <span className="font-mono text-sm text-blue-700 font-bold">{condition.sourceField}</span>
                                <span className="text-[10px] text-blue-500 uppercase font-semibold">
                                    from {sourceFiles?.find(f => f.id === condition.sourceFileId)?.name || 'selected file'}
                                </span>
                            </div>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    onClear();
                                }}
                                className="p-1 hover:bg-blue-100 rounded-full text-blue-300 hover:text-blue-500 transition-colors"
                            >
                                <XCircleIcon className="w-6 h-6" />
                            </button>
                        </div>
                    ) : (
                        <div className="text-center text-blue-400 text-sm flex flex-col items-center gap-1 group">
                            <SparklesIcon className="w-5 h-5 opacity-40 group-hover:scale-110 transition-transform" />
                            <span className="font-medium">Drop source field here to filter records</span>
                        </div>
                    )}
                </div>

                <div className="flex-[2] w-full">
                    <label className="block text-[10px] font-bold text-blue-900 uppercase mb-1 ml-1 tracking-wider opacity-70">
                        Allowed Values (comma separated)
                    </label>
                    <input
                        type="text"
                        disabled={!condition?.sourceField}
                        className={`w-full px-3 py-2 border rounded-lg text-sm font-medium transition-all outline-none shadow-inner
                            ${condition?.sourceField
                                ? 'bg-white border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-blue-900 px-4 h-11'
                                : 'bg-gray-100 border-gray-100 cursor-not-allowed opacity-50 h-11'
                            }
                        `}
                        placeholder={condition?.sourceField ? "e.g. CZE, SVK, POL" : "First map a source field..."}
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        onBlur={handleBlur}
                        onKeyDown={e => {
                            if (e.key === 'Enter') handleBlur();
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

const MappingCanvasV3: React.FC<WizardComponentProps> = ({ isEmbedded }) => {
    const { customerId, phaseId } = useParams<{ customerId: string; phaseId: string }>();
    const queryClient = useQueryClient();

    const [activeStep, setActiveStep] = useState(0); // 0: Identity, 1: Field Mapping, 2: Review

    // Mapping State
    const [currentEntityIndex, setCurrentEntityIndex] = useState(0);
    const [entities, setEntities] = useState<Entity[]>([]);
    const [entityMappings, setEntityMappings] = useState<{ [entityName: string]: EntityMappingV2 }>({});
    const [sourceFields, setSourceFields] = useState<SourceField[]>([]);
    const [currentEntityFields, setCurrentEntityFields] = useState<TargetField[]>([]);

    // Value Mapping Modal State
    const [isValueMappingOpen, setIsValueMappingOpen] = useState(false);
    const [activeValueMappingField, setActiveValueMappingField] = useState<TargetField | null>(null);
    const [selectedSourceFileId, setSelectedSourceFileId] = useState<string | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewDataMap, setPreviewDataMap] = useState<Record<string, MappingPreviewSnapshot>>({});

    const currentEntity = entities[currentEntityIndex];
    console.log('MappingCanvasV3 Render:', {
        index: currentEntityIndex,
        entity: currentEntity?.Entity,
        object: currentEntity?.Object,
        fieldsCount: currentEntityFields.length
    });

    // Helper: Get Current Mapping
    const currentMapping = useMemo(() => {
        if (!currentEntity) return undefined;
        const existing = entityMappings[currentEntity.Entity];
        if (existing) return existing;
        return {
            entityName: currentEntity.Entity,
            fieldMappings: [],
            valueMappings: {},
            rules: [],
            status: MappingStatus.DRAFT,
            isDraft: true,
            lastModified: new Date().toISOString()
        } as EntityMappingV2;
    }, [entityMappings, currentEntity]);

    // Queries
    const { data: selectedEntities } = useQuery({
        queryKey: ['phaseEntities', customerId, phaseId],
        queryFn: async () => {
            const res = await apiClient.getPhaseEntities(customerId!, phaseId!);
            return res.filter((e: any) => e.selected).sort((a: any, b: any) => a.Step - b.Step);
        },
        enabled: !!(customerId && phaseId)
    });

    const { data: sharePointSelection } = useQuery({
        queryKey: ['sharepointSelection', customerId, phaseId],
        queryFn: () => apiClient.getSharePointSelection(customerId!, phaseId!),
        enabled: !!(customerId && phaseId)
    });

    const { data: metadata } = useQuery({
        queryKey: ['metadata', customerId, phaseId],
        queryFn: () => apiClient.getMetadata(customerId!, phaseId!),
        enabled: !!(customerId && phaseId)
    });

    const { data: existingEntityMappings } = useQuery({
        queryKey: ['entityMappingsV2', customerId, phaseId],
        queryFn: () => apiClient.getEntityMappingsV2(customerId!, phaseId!),
        enabled: !!(customerId && phaseId)
    });

    const { data: existingEmployeeIdMappings } = useQuery({
        queryKey: ['employeeIdMappings', customerId, phaseId],
        queryFn: () => apiClient.getEmployeeIdMappings(customerId!, phaseId!),
        enabled: !!(customerId && phaseId)
    });

    const { data: phases } = useQuery({
        queryKey: ['phases', customerId],
        queryFn: () => apiClient.getPhases(customerId!),
        enabled: !!customerId
    });

    const currentPhase = useMemo(() => phases?.find((p: any) => p.id === phaseId), [phases, phaseId]);

    // Effects
    useEffect(() => {
        if (sharePointSelection) {
            const fields: SourceField[] = [];
            const seen = new Set();
            sharePointSelection.forEach((file: any) => {
                (file.columns || []).forEach((col: any) => {
                    const name = typeof col === 'string' ? col : (col.name || 'col');
                    const key = `${name}-${file.id}`;
                    if (!seen.has(key)) {
                        seen.add(key);
                        fields.push({
                            name,
                            dataType: typeof col === 'object' ? col.dataType : 'string',
                            sampleValues: typeof col === 'object' ? col.sampleValues : [],
                            originFile: file.name,
                            fileId: file.id
                        });
                    }
                });
            });
            setSourceFields(fields);
        }
    }, [sharePointSelection]);

    useEffect(() => {
        if (selectedEntities) setEntities(selectedEntities);
    }, [selectedEntities]);

    useEffect(() => {
        if (metadata && currentEntity) {
            const entName = currentEntity.Entity;
            const meta = metadata[entName];
            if (meta) {
                const fields = meta.fields.map((f: any) => ({
                    name: f.name,
                    label: f.label,
                    dataType: f.dataType,
                    required: f.required,
                    picklistId: f.picklistId,
                    picklistData: f.picklistData,
                    defaultValue: f.defaultValue
                }));


                setCurrentEntityFields(fields);
            } else {
                // Entity metadata not yet fetched — clear stale fields from a previously selected entity
                console.warn(`[MappingCanvasV3] No metadata found for entity "${entName}". Please refresh metadata.`);
                setCurrentEntityFields([]);
            }
        } else if (!currentEntity) {
            setCurrentEntityFields([]);
        }
    }, [metadata, currentEntity]);

    useEffect(() => {
        if (existingEntityMappings) {
            const map: any = {};
            existingEntityMappings.forEach((m: any) => {
                // Ensure fieldMappings is always present (handle potential V1 structure)
                const fieldMappings = m.fieldMappings || m.mappings || [];
                map[m.entityName] = {
                    ...m,
                    fieldMappings
                };
            });
            setEntityMappings(map);
        }
    }, [existingEntityMappings]);

    // Auto-map identifiers from Identity Step & FTSD
    useEffect(() => {
        if (!currentEntity || !currentEntityFields.length) {
            return;
        }

        // Refinement: Only run auto-map if no mapping exists in blob storage for this entity
        const entName = currentEntity.Entity;
        if (existingEntityMappings && existingEntityMappings.some((m: any) => m.entityName === entName)) {
            console.log(`[AutoMap] Skipping ${entName} - mapping already exists in blob storage`);
            return;
        }

        const mappingsList = existingEmployeeIdMappings
            ? (Array.isArray(existingEmployeeIdMappings?.mappings)
                ? existingEmployeeIdMappings.mappings
                : (Array.isArray(existingEmployeeIdMappings) ? existingEmployeeIdMappings : []))
            : [];

        setEntityMappings(prev => {
            const entName = currentEntity.Entity;
            const currentMap = prev[entName] || {
                entityName: entName,
                fieldMappings: [],
                valueMappings: {},
                rules: [],
                status: MappingStatus.DRAFT,
                isDraft: true,
                lastModified: new Date().toISOString()
            };

            const existingFieldMappings = currentMap.fieldMappings || [];
            let modified = false;

            // CLEANUP: Remove mappings for fields that no longer exist in metadata
            const validFieldMappings = existingFieldMappings.filter(m =>
                currentEntityFields.some(f => f.name === m.targetField)
            );

            if (validFieldMappings.length !== existingFieldMappings.length) {
                modified = true;
                console.log(`[AutoCleanup] Removed ${existingFieldMappings.length - validFieldMappings.length} invalid mappings for ${entName}`);
            }

            const newFieldMappings = [...validFieldMappings];

            // 1. Map Identifiers (userId, personIdExternal)
            // Also treat 'worker' / '*_worker' as target-field aliases for userId
            if (mappingsList.length > 0) {
                // Standard identifiers
                const identifierPairs: Array<{ targetKey: string; sourceKey: string }> = [
                    { targetKey: 'userId', sourceKey: 'userIdColumn' },
                    { targetKey: 'personIdExternal', sourceKey: 'personIdExternalColumn' }
                ];

                // Track what source was resolved for userId so we can reuse it for 'worker' fields
                let userIdSource: { sourceField: string; sourceFileId: string } | null = null;

                for (const { targetKey, sourceKey } of identifierPairs) {
                    const targetField = currentEntityFields.find(f => f.name === targetKey);
                    if (!targetField) continue;
                    if (existingFieldMappings.some(m => m.targetField === targetKey)) {
                        // Already mapped — capture the source so 'worker' can reuse it
                        if (targetKey === 'userId') {
                            const existing = existingFieldMappings.find(m => m.targetField === 'userId');
                            if (existing?.sourceField) {
                                userIdSource = { sourceField: existing.sourceField, sourceFileId: existing.sourceFileId || '' };
                            }
                        }
                        continue;
                    }

                    const identityMapping = mappingsList.find((m: any) => m[sourceKey]);
                    if (identityMapping) {
                        newFieldMappings.push({
                            id: `auto-${Date.now()}-${targetKey}`,
                            targetField: targetKey,
                            sourceField: identityMapping[sourceKey],
                            sourceFileId: identityMapping.fileId,
                            mappingType: MappingType.DIRECT,
                            required: targetField.required,
                            status: ValidationStatus.NOT_VALIDATED
                        });
                        modified = true;

                        if (targetKey === 'userId') {
                            userIdSource = { sourceField: identityMapping[sourceKey], sourceFileId: identityMapping.fileId };
                        }
                    }
                }

                // Fallback: if this entity has no 'userId' field (e.g. PaymentInformationV3, PaymentInformationDetailV3),
                // still resolve userIdSource from the identity mappings list so worker fields can be auto-mapped
                if (!userIdSource) {
                    const identityWithUserId = mappingsList.find((m: any) => m.userIdColumn);
                    if (identityWithUserId) {
                        userIdSource = { sourceField: identityWithUserId.userIdColumn, sourceFileId: identityWithUserId.fileId };
                    }
                }

                // 'worker' and any '*_worker' fields are treated as userId aliases
                // Covers: PaymentInformationV3.worker, PaymentInformationDetailV3.PaymentInformationV3_worker,
                // and country-specific extensions (e.g. PaymentInformationDetailV3_CZE.PaymentInformationV3_worker)
                if (userIdSource) {
                    const workerFields = currentEntityFields.filter(
                        f => f.name === 'worker' || f.name.endsWith('_worker')
                    );
                    for (const workerField of workerFields) {
                        if (existingFieldMappings.some(m => m.targetField === workerField.name)) continue;
                        newFieldMappings.push({
                            id: `auto-${Date.now()}-${workerField.name}`,
                            targetField: workerField.name,
                            sourceField: userIdSource.sourceField,
                            sourceFileId: userIdSource.sourceFileId,
                            mappingType: MappingType.DIRECT,
                            required: workerField.required,
                            status: ValidationStatus.NOT_VALIDATED
                        });
                        modified = true;
                    }
                }
            }

            // 2. Map FTSD to startDate (Except EmpJob)
            if (currentPhase?.ftsd && currentEntity.Entity !== 'EmpJob') {
                const targetKey = 'startDate';
                const targetField = currentEntityFields.find(f => f.name === targetKey);

                if (targetField && !existingFieldMappings.some(m => m.targetField === targetKey)) {
                    newFieldMappings.push({
                        id: `auto-ftsd-${Date.now()}`,
                        targetField: targetKey,
                        constantValue: currentPhase.ftsd,
                        mappingType: MappingType.CONSTANT,
                        required: targetField.required, // Use metadata requirement
                        status: ValidationStatus.NOT_VALIDATED
                    });
                    console.log(`[AutoMap] Mapped FTSD ${currentPhase.ftsd} to ${targetKey} for ${entName}`);
                    modified = true;
                }
            }

            if (!modified) return prev;

            return {
                ...prev,
                [entName]: {
                    ...currentMap,
                    fieldMappings: newFieldMappings
                }
            };
        });
    }, [currentEntity?.Entity, currentEntityFields, existingEmployeeIdMappings, currentPhase, existingEntityMappings]);

    // Actions
    const handleDrop = (entityName: string, targetFieldName: string, source: { name: string, fileId?: string }) => {
        console.log('handleDrop called:', entityName, targetFieldName, source);

        setEntityMappings(prev => {
            const entName = entityName;
            console.log('handleDrop updating entity:', entName);
            toast.success(`Mapping ${targetFieldName} to ${entName}`);

            const realMapState = prev[entName] || {
                entityName: entName,
                fieldMappings: [],
                valueMappings: {},
                rules: [],
                status: MappingStatus.DRAFT,
                isDraft: true,
                lastModified: new Date().toISOString()
            };

            if (realMapState.status === MappingStatus.COMPLETE || realMapState.status === MappingStatus.LOCKED) {
                toast.error("This entity is finalized and locked.");
                return prev;
            }

            const existingMappings = realMapState.fieldMappings || [];
            const idx = existingMappings.findIndex(m => m.targetField === targetFieldName);

            const newMapping: FieldMappingTableRow = {
                id: idx >= 0 ? existingMappings[idx].id : `map-${Date.now()}`,
                targetField: targetFieldName,
                sourceField: source.name,
                sourceFileId: source.fileId,
                mappingType: MappingType.DIRECT,
                required: currentEntityFields.find(f => f.name === targetFieldName)?.required || false,
                status: ValidationStatus.NOT_VALIDATED
            };

            const newFieldMappings = [...existingMappings];
            if (idx >= 0) newFieldMappings[idx] = newMapping;
            else newFieldMappings.push(newMapping);

            return {
                ...prev,
                [entName]: {
                    ...realMapState,
                    entityName: entName,
                    fieldMappings: newFieldMappings,
                    lastModified: new Date().toISOString(),
                    status: MappingStatus.IN_PROGRESS
                }
            };
        });
    };

    const handleClear = (entityName: string, targetFieldName: string) => {
        setEntityMappings(prev => {
            const entName = entityName;
            const currentMapState = prev[entName]; // If it's empty we can't clear anything anyway
            if (!currentMapState || !currentMapState.fieldMappings) return prev;

            if (currentMapState.status === MappingStatus.COMPLETE || currentMapState.status === MappingStatus.LOCKED) {
                toast.error("This entity is finalized and locked.");
                return prev;
            }

            return {
                ...prev,
                [entName]: {
                    ...currentMapState,
                    entityName: entName,
                    fieldMappings: currentMapState.fieldMappings.filter(m => m.targetField !== targetFieldName)
                }
            };
        });
    };

    const handleSetConstant = (entityName: string, targetFieldName: string, value: string) => {
        setEntityMappings(prev => {
            const entName = entityName;
            const realMapState = prev[entName] || {
                entityName: entName,
                fieldMappings: [],
                valueMappings: {},
                rules: [],
                status: MappingStatus.DRAFT,
                isDraft: true,
                lastModified: new Date().toISOString()
            };

            if (realMapState.status === MappingStatus.COMPLETE || realMapState.status === MappingStatus.LOCKED) {
                toast.error("This entity is finalized and locked.");
                return prev;
            }

            const existingMappings = realMapState.fieldMappings || [];
            const idx = existingMappings.findIndex(m => m.targetField === targetFieldName);

            const newMapping: FieldMappingTableRow = {
                id: idx >= 0 ? existingMappings[idx].id : `map-const-${Date.now()}`,
                targetField: targetFieldName,
                constantValue: value,
                mappingType: MappingType.CONSTANT,
                required: currentEntityFields.find(f => f.name === targetFieldName)?.required || false,
                status: ValidationStatus.NOT_VALIDATED
            };

            const newFieldMappings = [...existingMappings];
            if (idx >= 0) newFieldMappings[idx] = newMapping;
            else newFieldMappings.push(newMapping);

            return {
                ...prev,
                [entName]: {
                    ...realMapState,
                    entityName: entName,
                    fieldMappings: newFieldMappings,
                    lastModified: new Date().toISOString(),
                    status: MappingStatus.IN_PROGRESS
                }
            };
        });
    };

    const handleFilterDrop = (entityName: string, source: { name: string, fileId?: string }) => {
        setEntityMappings(prev => {
            const currentMapState = prev[entityName] || {
                entityName: entityName,
                fieldMappings: [],
                valueMappings: {},
                rules: [],
                status: MappingStatus.DRAFT,
                isDraft: true,
                lastModified: new Date().toISOString()
            };

            return {
                ...prev,
                [entityName]: {
                    ...currentMapState,
                    filterCondition: {
                        sourceField: source.name,
                        sourceFileId: source.fileId || '',
                        operator: 'IN',
                        values: currentMapState.filterCondition?.values || []
                    },
                    lastModified: new Date().toISOString()
                }
            };
        });
    };

    const handleFilterClear = (entityName: string) => {
        setEntityMappings(prev => {
            const currentMapState = prev[entityName];
            if (!currentMapState) return prev;

            const updated = { ...currentMapState };
            delete updated.filterCondition;

            return {
                ...prev,
                [entityName]: updated
            };
        });
    };

    const handleFilterUpdateValues = (entityName: string, values: string[]) => {
        setEntityMappings(prev => {
            const currentMapState = prev[entityName];
            if (!currentMapState || !currentMapState.filterCondition) return prev;

            return {
                ...prev,
                [entityName]: {
                    ...currentMapState,
                    filterCondition: {
                        ...currentMapState.filterCondition,
                        values
                    },
                    lastModified: new Date().toISOString()
                }
            };
        });
    };

    const saveMutation = useMutation({
        mutationFn: (mapping: EntityMappingV2) => apiClient.saveEntityMappingV2(customerId!, phaseId!, mapping),
        onSuccess: () => {
            toast.success('Mapping saved');
            queryClient.invalidateQueries({ queryKey: ['entityMappingsV2'] });
        }
    });

    const generatePreviewMutation = useMutation({
        mutationFn: (entityName: string) => {
            const mapping = entityMappings[entityName];
            return apiClient.generateMappingPreview(customerId!, phaseId!, entityName, mapping, 'UI');
        },
        onSuccess: (data) => {
            setPreviewDataMap(prev => ({
                ...prev,
                [data.preview.entityName]: data.preview
            }));
            toast.success('Preview generated');
            setIsPreviewOpen(true);
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error?.message || 'Failed to generate preview');
        }
    });

    const generateExportMutation = useMutation({
        mutationFn: async () => {
            const config = {
                format: 'csv',
                entityOrder: entities.map(e => e.Entity)
            };
            const mappingsList = Object.values(entityMappings);
            return await apiClient.createExport(customerId!, phaseId!, config, mappingsList);
        },
        onSuccess: (data) => {
            toast.success('Export generated successfully');
            apiClient.getExportResult(customerId!, phaseId!, data.id).then(async (result) => {
                if (result.status === 'completed') {
                    const downloadRes = await apiClient.get<Blob>(`/export/${customerId}/${phaseId}/${data.id}/download`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([downloadRes.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    const isZip = downloadRes.headers['content-type']?.includes('zip') || true;
                    // defaulting to zip if undetected because backend usually zips multiple csvs
                    const ext = isZip ? 'zip' : 'csv';
                    link.setAttribute('download', `migration_export_${new Date().toISOString().split('T')[0]}.${ext}`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }
            });
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.error?.message || 'Failed to generate export');
        }
    });

    const validateMutation = useMutation({
        mutationFn: (data: { entityName: string, fieldMappings: any[] }) =>
            apiClient.validateEntityMappingV2(customerId!, phaseId!, data.entityName, data.fieldMappings),
        onError: (err: any) => toast.error(`Validation failed: ${err.message}`)
    });

    const finalizeEntity = async () => {
        if (!currentEntity || !currentMapping) return;

        // 0. Required Fields Validation
        const missingRequiredFields = currentEntityFields
            .filter(f => f.required)
            .filter(f => {
                const mapping = currentMapping.fieldMappings.find(m => m.targetField === f.name);
                return !mapping || (!mapping.sourceField && mapping.mappingType !== MappingType.CONSTANT);
            });

        if (missingRequiredFields.length > 0) {
            toast.error(`Cannot finalize: Missing required fields: ${missingRequiredFields.map(f => f.label).join(', ')}`);
            return;
        }

        // 1. Server-side Validation
        try {
            const validation = await validateMutation.mutateAsync({
                entityName: currentEntity.Entity,
                fieldMappings: currentMapping.fieldMappings
            });

            if (validation.summary?.hasErrors) {
                toast.error(`Cannot finalize: ${validation.summary.invalidMappings} fields have errors.`);
                // Show specific errors
                validation.results.filter((r: any) => !r.valid).forEach((r: any) => {
                    r.errors.forEach((e: string) => toast.error(`${r.targetField}: ${e}`));
                });
                return;
            }
        } catch (e) {
            return; // Error handled in mutation onError
        }

        // 2. Picklist Validation (Check all source values mapped)
        const picklistErrors: string[] = [];
        currentMapping.fieldMappings.forEach(fm => {
            const targetDef = currentEntityFields.find(f => f.name === fm.targetField);
            // Check if it has picklist (either picklistId propery or picklistData)
            const hasPicklist = !!(targetDef?.picklistId || targetDef?.picklistData);

            if (hasPicklist && fm.sourceField && fm.mappingType === MappingType.DIRECT) {
                const sourceDef = sourceFields.find(s => s.name === fm.sourceField);
                // Try to get sample values. If not available in sourceDef, try to find in mapping preview if available
                const values = sourceDef?.sampleValues || [];

                // valueMappings for this field
                const mappedRows = currentMapping.valueMappings?.[fm.targetField] || [];

                const unmapped = values.filter(val => !mappedRows.some((vm: any) => vm.sourceValue === val));
                if (unmapped.length > 0) {
                    picklistErrors.push(`Field ${fm.targetField}: Missing mappings for values: ${unmapped.slice(0, 3).join(', ')}${unmapped.length > 3 ? '...' : ''}`);
                }
            }
        });

        if (picklistErrors.length > 0) {
            picklistErrors.forEach(e => toast.error(e));
            toast.error("Please map all source values for picklist fields before finalizing.");
            return;
        }

        const updatedMapping = {
            ...currentMapping,
            status: MappingStatus.COMPLETE,
            lastModified: new Date().toISOString()
        };

        setEntityMappings(prev => ({
            ...prev,
            [currentEntity.Entity]: updatedMapping
        }));

        saveMutation.mutate(updatedMapping);
        toast.success(`Finalized ${currentEntity.Object}`);
    };

    const unlockEntity = () => {
        if (!currentEntity || !currentMapping) return;
        const updatedMapping = {
            ...currentMapping,
            status: MappingStatus.IN_PROGRESS,
            lastModified: new Date().toISOString()
        };
        setEntityMappings(prev => ({
            ...prev,
            [currentEntity.Entity]: updatedMapping
        }));
        saveMutation.mutate(updatedMapping);
        toast.success(`Unlocked ${currentEntity.Object}`);
    };

    // Steps Rendering
    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                        <EmployeeIdMappingStep
                            customerId={customerId!}
                            phaseId={phaseId!}
                            sharePointSelection={sharePointSelection || []}
                            existingMappings={
                                Array.isArray(existingEmployeeIdMappings?.mappings)
                                    ? existingEmployeeIdMappings.mappings
                                    : (Array.isArray(existingEmployeeIdMappings) ? existingEmployeeIdMappings : [])
                            }
                            onComplete={(mappings, result) => {
                                toast.success("Identity Mapping Completed");
                                setActiveStep(1);
                            }}
                        />
                    </div>
                );
            case 1:
                return (
                    <div className="flex gap-6 h-[calc(100vh-280px)] min-h-[500px]">
                        {/* Sidebar Left: Source Fields */}
                        <div className="w-64 flex-shrink-0 flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-3 bg-gray-50 border-b border-gray-200">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Source Fields</h3>
                                <select
                                    value={selectedSourceFileId || ''}
                                    onChange={(e) => setSelectedSourceFileId(e.target.value || null)}
                                    className="w-full text-xs border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All Files</option>
                                    {sharePointSelection?.map((file: any) => (
                                        <option key={file.id} value={file.id} title={file.name}>{file.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
                                {sourceFields
                                    .filter(f => !selectedSourceFileId || f.fileId === selectedSourceFileId)
                                    .map((f, i) => (
                                        <DraggableSourceField key={`${f.fileId}-${f.name}`} field={f} />
                                    ))}
                            </div>
                        </div>

                        {/* Main Canvas: Target Fields */}
                        <div className="flex-1 min-w-0 flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            {/* Entity Selector Toolbar */}
                            <div className="p-4 border-b border-gray-200 flex flex-wrap lg:flex-nowrap items-center justify-between bg-gray-50 gap-4">
                                <div className="flex items-center gap-4 flex-shrink-0">
                                    <label className="text-sm font-medium text-gray-600">Current Object:</label>
                                    <select
                                        value={currentEntityIndex}
                                        onChange={e => setCurrentEntityIndex(Number(e.target.value))}
                                        className="pl-3 pr-8 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-semibold text-gray-800 shadow-sm focus:ring-2 focus:ring-blue-500"
                                    >
                                        {entities.map((e, i) => {
                                            const status = entityMappings[e.Entity]?.status;
                                            const isFinalized = status === MappingStatus.COMPLETE || status === MappingStatus.LOCKED;
                                            return (
                                                <option
                                                    key={i}
                                                    value={i}
                                                    className={isFinalized ? "text-gray-400 bg-gray-50 italic" : "text-gray-900"}
                                                >
                                                    {e.Object} ({e.Entity}) {isFinalized ? '🔒' : ''}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 overflow-x-auto">
                                    <button onClick={() => generatePreviewMutation.mutate(currentEntity.Entity)} className="btn-secondary text-sm py-1.5 flex items-center gap-2">
                                        <TableCellsIcon className="w-4 h-4" /> Preview
                                    </button>
                                    <button onClick={() => generateExportMutation.mutate()} className="btn-secondary text-sm py-1.5 flex items-center gap-2">
                                        <ArrowDownTrayIcon className="w-4 h-4" /> Export CSV
                                    </button>

                                    {currentMapping?.status === MappingStatus.COMPLETE ? (
                                        <div className="flex items-center gap-2">
                                            <span className="text-green-600 text-sm font-medium flex items-center gap-1 border px-3 py-1.5 rounded-md bg-green-50 border-green-200">
                                                <LockClosedIcon className="w-4 h-4" /> Finalized
                                            </span>
                                            <button onClick={unlockEntity} className="btn-secondary text-sm py-1.5 flex items-center gap-2 text-gray-600 hover:text-gray-900">
                                                <LockOpenIcon className="w-4 h-4" /> Unlock
                                            </button>
                                        </div>
                                    ) : (
                                        <button onClick={finalizeEntity} className="btn-secondary text-sm py-1.5 flex items-center gap-2 text-green-700 border-green-200 hover:bg-green-50">
                                            <CheckCircleIcon className="w-4 h-4" /> Finalize
                                        </button>
                                    )}

                                    <button onClick={() => saveMutation.mutate(currentMapping!)} className="btn-secondary text-sm py-1.5">Save Draft</button>
                                    <button onClick={() => setActiveStep(2)} className="btn-primary text-sm py-1.5 flex items-center">
                                        Review & Finish <ArrowRightIcon className="w-4 h-4 ml-1" />
                                    </button>
                                </div>
                            </div>

                            {/* Mapping List */}
                            <div className="flex-1 overflow-auto p-6 custom-scrollbar bg-gray-50/50">
                                <div className="min-w-[800px] w-full mx-auto">
                                    {/* Global Filter for Country Specific Entities */}
                                    {currentEntity && isCountrySpecificEntity(currentEntity.Entity) && (
                                        <EntityFilterSection
                                            condition={currentMapping?.filterCondition}
                                            onDrop={(src) => handleFilterDrop(currentEntity.Entity, src)}
                                            onClear={() => handleFilterClear(currentEntity.Entity)}
                                            onUpdateValues={(vals) => handleFilterUpdateValues(currentEntity.Entity, vals)}
                                            sourceFiles={sharePointSelection}
                                        />
                                    )}

                                    {currentEntityFields.length === 0 ? (
                                        <div className="text-center py-16 text-gray-500">
                                            <ExclamationTriangleIcon className="w-12 h-12 mx-auto mb-3 text-amber-400" />
                                            <p className="text-lg font-medium text-gray-700">No fields loaded for {currentEntity?.Entity}</p>
                                            <p className="text-sm mt-1">Please go to the <span className="font-semibold">Metadata</span> step and click <span className="font-semibold">Refresh Metadata</span> to fetch the field definitions for this entity.</p>
                                        </div>
                                    ) : currentEntityFields.map(f => (
                                        <MappingRowV3
                                            key={f.name}
                                            targetField={f}
                                            mapping={currentMapping?.fieldMappings.find(m => m.targetField === f.name)}
                                            sourceFiles={sharePointSelection}
                                            onDrop={(src) => handleDrop(currentEntity.Entity, f.name, src)}
                                            onClear={() => handleClear(currentEntity.Entity, f.name)}
                                            onSetConstant={(val) => handleSetConstant(currentEntity.Entity, f.name, val)}
                                            onOpenValueMapping={() => {
                                                setActiveValueMappingField(f);
                                                setIsValueMappingOpen(true);
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-3xl mx-auto text-center">
                        <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Mapping Configuration Ready</h2>
                        <p className="text-gray-600 mb-8">
                            You have configured identifiers and field mappings. You can now proceed to the migration execution phase.
                        </p>
                        <div className="flex justify-center gap-4">
                            <button onClick={() => setActiveStep(1)} className="btn-secondary">Back to Editing</button>
                            <button className="btn-primary" onClick={() => toast.success("Process Completed!")}>Finalize Configuration</button>
                        </div>
                    </div>
                );
            default: return null;
        }
    };


    // Step Counts Calculations
    const totalFiles = sharePointSelection?.length || 0;
    const mappedFilesCount = useMemo(() => {
        const list = existingEmployeeIdMappings
            ? (Array.isArray(existingEmployeeIdMappings?.mappings)
                ? existingEmployeeIdMappings.mappings
                : (Array.isArray(existingEmployeeIdMappings) ? existingEmployeeIdMappings : []))
            : [];
        return list.filter((m: any) => m.userIdColumn || m.personIdExternalColumn).length;
    }, [existingEmployeeIdMappings]);

    const totalEntities = entities.length;
    const mappedEntitiesCount = useMemo(() => {
        return Object.values(entityMappings).filter(m =>
            m.status === MappingStatus.COMPLETE || m.status === MappingStatus.LOCKED
        ).length;
    }, [entityMappings]);

    const steps = [
        { label: 'Identify & Resolution', count: `${mappedFilesCount}/${totalFiles} files` },
        { label: 'Field Mapping & Values', count: `${mappedEntitiesCount}/${totalEntities} entities` },
        { label: 'Review', count: '' }
    ];

    return (
        <div className="w-full mx-auto px-4 py-6">
            {!isEmbedded && (
                <>
                    {/* Breadcrumb Navigation */}
                    <nav className="mb-4 flex items-center text-sm text-gray-600">
                        <Link to="/customers" className="hover:text-primary-600 transition-colors">
                            Customers
                        </Link>
                        <span className="mx-2">/</span>
                        <Link
                            to={`/customers/${customerId}/phases`}
                            className="hover:text-primary-600 transition-colors"
                        >
                            Phases
                        </Link>
                        <span className="mx-2">/</span>
                        <Link
                            to={`/customers/${customerId}/phases/${phaseId}`}
                            className="hover:text-primary-600 transition-colors"
                        >
                            Phase Dashboard
                        </Link>
                        <span className="mx-2">/</span>
                        <span className="text-gray-900 font-medium">Field Mapping</span>
                    </nav>

                    {/* Back Button */}
                    <Link
                        to={`/customers/${customerId}/phases/${phaseId}`}
                        className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium mb-4 transition-colors"
                    >
                        <ChevronLeftIcon className="w-4 h-4 mr-1" />
                        Back to Phase Dashboard
                    </Link>

                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-900">Migration Mapping (V3 Wizard)</h1>
                        <p className="text-gray-500">Step-by-step guided mapping configuration.</p>
                    </div>
                </>
            )}

            <Stepper
                currentStep={activeStep}
                steps={steps}
                onStepClick={setActiveStep}
            />

            {renderStepContent()}

            {/* Value Mapping Modal */}
            {isValueMappingOpen && activeValueMappingField && (
                <ValueMappingModalWrapper
                    customerId={customerId!}
                    phaseId={phaseId!}
                    isOpen={isValueMappingOpen}
                    onClose={() => setIsValueMappingOpen(false)}
                    currentEntity={currentEntity!}
                    activeField={activeValueMappingField}
                    currentMapping={currentMapping}
                    setEntityMappings={setEntityMappings}
                />
            )}

            {isPreviewOpen && currentEntity && previewDataMap[currentEntity.Entity] && (
                <MappingPreviewModal
                    preview={previewDataMap[currentEntity.Entity]}
                    onClose={() => setIsPreviewOpen(false)}
                />
            )}
        </div>
    );
};



// Wrapper to handle data fetching for Value Mapping Modal
const ValueMappingModalWrapper: React.FC<{
    customerId: string;
    phaseId: string;
    isOpen: boolean;
    onClose: () => void;
    currentEntity: Entity;
    activeField: TargetField;
    currentMapping?: EntityMappingV2;
    setEntityMappings: React.Dispatch<React.SetStateAction<{ [entityName: string]: EntityMappingV2 }>>;
}> = ({ customerId, phaseId, isOpen, onClose, currentEntity, activeField, currentMapping, setEntityMappings }) => {

    const sourceColumn = currentMapping?.fieldMappings.find(m => m.targetField === activeField.name)?.sourceField;

    // Fetch Source Values
    const { data: sourceValuesResponse } = useQuery({
        queryKey: ['sourceValues', customerId, phaseId, currentEntity.Entity, activeField.name, sourceColumn],
        queryFn: () => apiClient.getSourceValues(customerId, phaseId, currentEntity.Entity, activeField.name, sourceColumn),
        enabled: isOpen && !!currentEntity && !!activeField && !!sourceColumn
    });

    // Picklist options are already in activeField.picklistData or we might need to fetch if empty
    // Assuming picklistData is populated from metadata. If not, we might need a separate endpoint.
    // metadata query in parent component should populate picklistData if available.

    return (
        <ValueMappingModal
            isOpen={isOpen}
            onClose={onClose}
            onSave={(valMappings) => {
                // Convert simple object to ValueMappingTableRow[]
                const valueRows: any[] = Object.entries(valMappings).map(([src, tgt], idx) => ({
                    id: `vm-${Date.now()}-${idx}`,
                    sourceValue: src,
                    targetValue: tgt,
                    status: 'mapped'
                }));

                // Save value mappings to current entity mapping
                setEntityMappings(prev => ({
                    ...prev,
                    [currentEntity.Entity]: {
                        ...currentMapping!,
                        valueMappings: {
                            ...currentMapping!.valueMappings,
                            [activeField.name]: valueRows
                        }
                    }
                }));
            }}
            entityName={currentEntity.Entity}
            fieldName={activeField.name}
            fieldLabel={activeField.label}
            sourceColumn={currentMapping?.fieldMappings.find(m => m.targetField === activeField.name)?.sourceField || ''}
            sourceValues={sourceValuesResponse?.sourceValues || []}
            picklistOptions={extractPicklistOptions(activeField)}
            picklistId={activeField.picklistId}
            existingMappings={
                // Convert array back to object for the modal
                (currentMapping?.valueMappings?.[activeField.name] || []).reduce((acc: any, row: any) => {
                    acc[row.sourceValue] = row.targetValue;
                    return acc;
                }, {})
            }
        />
    );
};

export default MappingCanvasV3;
